import { SelectProps } from 'antd';
import { DefaultOptionType } from 'antd/es/cascader';
import Select, { BaseOptionType } from 'antd/es/select';
import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getEnumKeyByValue } from 'src/app/helpers/enum.helper';

import { InterestsTypes } from 'src/enums/interests.enum';

export type LazyGetTriggerType = any;

interface DebounceSelectProps<ValueType> extends Omit<SelectProps<ValueType | ValueType[]>, 'options' | 'children'> {
  fetchOptions: LazyGetTriggerType;
  debounceTimeout?: number;
}

// This component is used to search interests in header
function DebounceSelect<ValueType extends BaseOptionType[]>({
  fetchOptions,
  debounceTimeout = 800,
  ...props
}: DebounceSelectProps<ValueType>) {
  const [options, setOptions] = useState<DefaultOptionType[]>([]);
  const fetchRef = useRef(0);

  useEffect(() => {
    if (!props.value && options.length) setOptions([]);
  }, [props.value]);

  /* 
   It fetches for typed text by debounce and filter result from interest list
  */
  const debounceFetcher = useMemo(() => {
    const loadOptions = (value: string) => {
      if (!value) {
        setOptions([]);
        return;
      }
      fetchRef.current += 1;
      const fetchId = fetchRef.current;
      setOptions([]);
      fetchOptions({ search: [value] })
        .then(({ data }: any) => {
          if (fetchId !== fetchRef.current) {
            return;
          }

          if (!data?.rows || data?.rows.length <= 0) {
            return;
          }

          const newOptions: DefaultOptionType[] = [];

          data.rows.forEach(({ interests }: any) => {
            interests.forEach((interest: any) => {
              newOptions.push({
                label: `${interest.value} (${getEnumKeyByValue(InterestsTypes, interest.type)})`,
                value: interest.id,
                type: interest.type,
                name: interest.value,
                classType: interest.classType,
              });
            });
          });
          setOptions(newOptions);
        })
        .catch((err: any) => console.log('err', err));
    };

    return debounce(loadOptions, debounceTimeout);
  }, [fetchOptions, debounceTimeout]);

  return (
    <Select
      autoClearSearchValue
      labelInValue
      filterOption={false}
      onSearch={debounceFetcher}
      //   notFoundContent={fetching ? <Spin size="small" /> : null}
      {...props}
      options={options}
    />
  );
}

export default DebounceSelect;
